import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { Textarea } from "@/components/core/forms/textarea";
import { useUpdateCollectionStatusMutation } from "@/features/dashboard/api/queries/sales-contract-query";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCalendar, IconCircleCheck, IconClock, IconUser } from "@tabler/icons-react";
import { format } from "date-fns";
import type React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const collectionStatusSchema = z.object({
	collection_status: z.enum(["pending", "collected"]),
	collection_remarks: z.string().optional(),
	date_collected: z.string().optional(),
	collected_by: z.string().optional(),
});

type CollectionStatusFormData = z.infer<typeof collectionStatusSchema>;

interface CollectionStatusModalProps {
	contract: SalesContract;
	onSuccess?: () => void;
}

export const CollectionStatusModal: React.FC<CollectionStatusModalProps> = ({ contract, onSuccess }) => {
	const { user } = useAuth();
	const updateMutation = useUpdateCollectionStatusMutation();

	const isCollected = contract.collection_status === "collected";

	const form = useForm<CollectionStatusFormData>({
		resolver: zodResolver(collectionStatusSchema),
		defaultValues: {
			collection_status: isCollected ? "pending" : "collected",
			collection_remarks: contract.collection_remarks || "",
			date_collected: contract.date_collected || format(new Date(), "yyyy-MM-dd"),
			collected_by: contract.collected_by || user?.name || `${user?.first_name} ${user?.last_name}` || "",
		},
	});

	const watchedStatus = form.watch("collection_status");

	const onSubmit = async (data: CollectionStatusFormData) => {
		try {
			await updateMutation.mutateAsync({
				id: contract.id,
				collection_status: data.collection_status,
				collection_remarks: data.collection_remarks || undefined,
				date_collected: data.collection_status === "collected" ? data.date_collected : undefined,
				collected_by: data.collection_status === "collected" ? data.collected_by : undefined,
			});

			onSuccess?.();
		} catch (error) {
			// Error handled by mutation
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
				<div className="flex flex-col w-full gap-6">
					{/* Contract Details Header */}
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<h3 className="font-medium text-blue-900 mb-2">Contract Details</h3>
						<div className="text-sm text-blue-800">
							<p>
								<strong>Project:</strong> {contract.project?.name}
							</p>
							<p>
								<strong>Monthly Recurring Value:</strong> ₱{formatNumber(contract.monthly_recurring_sales)}
							</p>
							{contract.project?.client && (
								<p>
									<strong>Client:</strong> {contract.project.client.company || contract.project.client.name}
								</p>
							)}
						</div>
					</div>

					{/* Status Selection */}
					<FormField
						control={form.control}
						name="collection_status"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Collection Status</FormLabel>
								<FormGroup>
									<div className="grid grid-cols-2 gap-3">
										<button
											type="button"
											onClick={() => field.onChange("collected")}
											className={`p-3 rounded-lg border-2 transition-all ${
												field.value === "collected" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="flex items-center justify-center gap-2">
												<IconCircleCheck className="w-5 h-5" />
												<span className="font-medium">Collected</span>
											</div>
										</button>
										<button
											type="button"
											onClick={() => field.onChange("pending")}
											className={`p-3 rounded-lg border-2 transition-all ${
												field.value === "pending" ? "border-yellow-500 bg-yellow-50 text-yellow-700" : "border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="flex items-center justify-center gap-2">
												<IconClock className="w-5 h-5" />
												<span className="font-medium">Pending</span>
											</div>
										</button>
									</div>
								</FormGroup>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* Collection Details - Only show when marking as collected */}
					{watchedStatus === "collected" && (
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-6">
								<FormField
									control={form.control}
									name="date_collected"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												<IconCalendar className="inline w-4 h-4 mr-1" />
												Date Collected
											</FormLabel>
											<FormGroup>
												<Input className="input" type="date" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-6">
								<FormField
									control={form.control}
									name="collected_by"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												<IconUser className="inline w-4 h-4 mr-1" />
												Collected By
											</FormLabel>
											<FormGroup>
												<Input className="input" {...field} placeholder="Name of person who collected" />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					)}

					{/* Remarks - Always show */}
					<FormField
						control={form.control}
						name="collection_remarks"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Remarks {watchedStatus === "pending" && "(e.g., reason for pending)"}</FormLabel>
								<FormGroup>
									<Textarea
										className="input"
										{...field}
										placeholder={watchedStatus === "collected" ? "Add any notes about the collection..." : "Add reason why collection is pending..."}
										rows={3}
									/>
								</FormGroup>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* Current Collection Info if exists */}
					{contract.collection_status === "collected" && contract.date_collected && (
						<div className="bg-blue-50 p-3 rounded-lg text-sm">
							<p className="font-medium text-blue-900 mb-1">Previous Collection Info:</p>
							<p className="text-blue-700">
								Collected on {formatDate(contract.date_collected, "PP")} by {contract.collected_by}
							</p>
							{contract.collection_remarks && <p className="text-blue-600 mt-1 italic">"{contract.collection_remarks}"</p>}
						</div>
					)}
				</div>

				<div className="flex justify-end gap-2 pt-4 border-t">
					<Button type="button" variant="outline" onClick={onSuccess}>
						Cancel
					</Button>
					<Button type="submit" disabled={updateMutation.isPending} className={watchedStatus === "collected" ? "bg-green-600 hover:bg-green-700" : ""}>
						{updateMutation.isPending ? "Updating..." : watchedStatus === "collected" ? "Mark as Collected" : "Mark as Pending"}
					</Button>
				</div>
			</form>
		</Form>
	);
};
